// eslint-disable-next-line import/no-unresolved
import $ from 'jquery';

import { restCall } from '../common/utilities.js';

// Import utilities
const {
	isDefined
} = _uo.utility;

/**
 * Listens to the Categories metabox to save the values using AJAX on change.
 * We need to do this because Automator doesn't have a "Save" button
 */
export default class Categories {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Check if the required element exist before adding the event listeners
		if ( this.hasMetabox ) {
			// Listen changes to the checkboxes
			this.listenCheckboxes();
		}
	}

	/**
	 * Returns the node of the container of the Categories metabox
	 *
	 * @return {Node} The categories metabox
	 */
	get $container() {
		return $( '#recipe_categorydiv' );
	}

	/**
	 * Returns the node of the container (ul) with the list
	 * of categories
	 *
	 * @return {Node} The list
	 */
	get $allCategoriesList() {
		return $( '#recipe_categorychecklist' );
	}

	/**
	 * Returns true if the required metabox exists
	 *
	 * @return {Boolean} True if "Categories" exists
	 */
	get hasMetabox() {
		return isDefined( this.$allCategoriesList );
	}

	/**
	 * Listens changes to all checkboxes in the Category metabox
	 *
	 * @return {undefined}
	 */
	listenCheckboxes() {
		// Listen changes to each checkbox
		this.$container.on(
			'change',
			'input[type="checkbox"][name="tax_input[recipe_category][]"], .popular-category input[type="checkbox"]',
			( event ) => {
				// Get checkbox
				const $checkbox = $( event.currentTarget );

				// Save category
				this.saveCategory( $checkbox );
			}
		);

		// Listen when the user adds a new category. This will be
		// triggered when WP creates a new category after the user
		// used the "+ Add New Category" form
		new MutationObserver( ( mutations ) => {
			// Iterate mutations
			mutations.forEach( ( mutation ) => {
				// Iterate new categories
				mutation.addedNodes.forEach( ( addedNode ) => {
					// Get the checkbox of the new category
					const $checkbox = $( addedNode ).find(
						'input[type="checkbox"][name="tax_input[recipe_category][]"]'
					);

					// Save category
					this.saveCategory( $checkbox );
				} );
			} );
		} ).observe( this.$allCategoriesList[ 0 ], {
			childList: true,
		} );
	}

	/**
	 * Saves the value of the category (checked/not checked)
	 *
	 * @param {Node} $checkbox The checkbox
	 * @return {undefined}
	 */
	saveCategory( $checkbox ) {
		// Get the data
		const checkboxData = {
			id: $checkbox.val(),
			isChecked: $checkbox.is( ':checked' ),
		};

		// Set checkbox as loading
		this.setStatus( 'loading', true, $checkbox );

		// Do call
		restCall(
			'set_recipe_terms',
			{
				// eslint-disable-next-line camelcase
				term_id: 'recipe_category',
				// eslint-disable-next-line camelcase
				recipe_id: UncannyAutomator.recipe.id,
				// eslint-disable-next-line camelcase
				category_id: checkboxData.id,
				// eslint-disable-next-line camelcase
				set_category: checkboxData.isChecked,
			},
			( response ) => {
				// If the call (not necessarily the task) was successful...

				if ( response.success ) {
					// If everything went well...
					// Show a green check icon
					this.setStatus( 'success', true, $checkbox );

					// Remove it after 2 seconds
					setTimeout( () => {
						// Remove the green check icon
						this.setStatus( 'success', false, $checkbox );
					}, 2000 );
				} else {
					// Output if the developer mode is enabled
					if ( UncannyAutomator.dev.developerMode ) {
						// eslint-disable-next-line no-console
						console.error( "Couldn't change the status of the recipe category" );
					}

					// If something went wrong,
					// revert the value of the checkbox
					this.revertCheckboxValue( $checkbox );

					// Show an error icon
					this.setStatus( 'error', true, $checkbox );
					// Remove it after 2 seconds
					setTimeout( () => {
						// Remove the error icon
						this.setStatus( 'error', false, $checkbox );
					} );
				}

				// Remove loading animation
				this.setStatus( 'loading', false, $checkbox );
			},
			() => {
				// On error...

				// Revert the value of the checkbox
				this.revertCheckboxValue( $checkbox );

				// Remove loading animation
				this.setStatus( 'loading', false, $checkbox );

				// Show an error icon
				this.setStatus( 'error', true, $checkbox );
				// Remove it after 2 seconds
				setTimeout( () => {
					// Remove the error icon
					this.setStatus( 'error', false, $checkbox );
				} );
			},
			{
				requiresRecipesObject: false,
				renderUI: false,
			}
		);
	}

	/**
	 * Reverts the value of the checkbox
	 *
	 * @param {Node} $checkbox The checkbox
	 * @return {undefined}
	 */
	revertCheckboxValue( $checkbox ) {
		// Revert the value of the checkbox
		$checkbox.prop( 'checked', ! $checkbox.is( ':checked' ) );
	}

	/**
	 * Sets the status of the metabox
	 *
	 * @param {String}  status    The status of the metabox
	 * @param {Boolean} enable    Defines the value of the status.
	 *                            For example, if status is "loading",
	 *                            and "enable" is true, then the checkbox
	 *                            is rendered with the loading animations.
	 * @param {Node}    $checkbox The checkbox of the category
	 * @return {undefined}
	 */
	setStatus( status = '', enable = true, $checkbox ) {
		// Get label container
		const $label = $checkbox.closest( '.selectit' );

		// Check what status we have to set or unset
		if ( status == 'loading' ) {
			// Check if we have to add or remove the loading status
			if ( enable ) {
				// Add loading animation
				$label.addClass( 'uap-taxonomy-loading' );

				// Disable checkbox
				$checkbox.prop( 'disabled', true );
			} else {
				// Remove loading animation
				$label.removeClass( 'uap-taxonomy-loading' );

				// Enable checkbox
				$checkbox.prop( 'disabled', false );
			}
		} else if ( status == 'success' ) {
			// Check if we have to add or remove the loading status
			if ( enable ) {
				// Add loading animation
				$label.addClass( 'uap-taxonomy-success' );
			} else {
				// Remove loading animation
				$label.removeClass( 'uap-taxonomy-success' );
			}
		} else if ( status == 'error' ) {
			// Check if we have to add or remove the loading status
			if ( enable ) {
				// Add loading animation
				$label.addClass( 'uap-taxonomy-error' );
			} else {
				// Remove loading animation
				$label.removeClass( 'uap-taxonomy-error' );
			}
		}
	}
}
